import { useQuery, useQueryClient } from '@tanstack/react-query';

import { type FiltersTrackingLocation, useTrackReturnedEmptyFiltersResult } from '@air/analytics';

import { getPrivateSearchTotalKey } from '~/constants/react-query-keys';
import { useFilterParams } from '~/hooks/filters/useFilterParams';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useFiltersContext } from '~/providers/FiltersProvider';
import { useClipSearchParams } from '~/swr-hooks/search/useClipSearchParams';
import { getTypesToFetchFromParams } from '~/swr-hooks/search/utils';
import { type SearchTotals } from '~/types/SearchTotals';
import { filterParamsToTrackingType } from '~/utils/filters/filtersToUrlParamsUtils';

export const usePrivateSearchTotals = ({ trackLocation }: { trackLocation: FiltersTrackingLocation }) => {
  const clipsFetchParams = useClipSearchParams();
  const { typesParam } = useFilterParams();
  const { trackReturnedEmptyFiltersResult } = useTrackReturnedEmptyFiltersResult();
  const queryClient = useQueryClient();
  clipsFetchParams.types = getTypesToFetchFromParams(typesParam);

  const { currentWorkspace } = useCurrentWorkspace();
  const workspaceId = currentWorkspace?.id;
  const {
    filters: { customFields, ...filters },
  } = useFiltersContext();

  const { data } = useQuery({
    queryKey: getPrivateSearchTotalKey(workspaceId),
    queryFn: async () => {
      const data = queryClient.getQueryData<SearchTotals>(getPrivateSearchTotalKey(workspaceId));

      const total = data?.total ?? 0;
      if (!data?.total) {
        trackReturnedEmptyFiltersResult({
          types: filterParamsToTrackingType({ ...filters, ...customFields }),
          location: trackLocation,
        });
      }
      return { total };
    },
    enabled: !!workspaceId,
  });

  return {
    total: data?.total,
  };
};
